import { Button, ChildrenProps, Column, If, Row, useUUID } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	BooleanField,
	DateTimeField,
	Form,
	FormType,
	Label,
	SubFormField,
	SubFormRenderProps,
	TextField,
	useCheckShowFieldValidation,
} from "@hex-insights/forms";
import { ExternalLink } from "@hex-insights/router";
import { Icon } from "../../../Components/Icon";
import {
	EnrollmentApplicationFormValues,
	EnrollmentApplicationGuardianFormValues,
	EnrollmentApplicationStudentFormValues,
} from "../../FormUtilities";
import { EnrollmentApplicationGuardianForm } from "../EnrollmentApplicationGuardian";
import { EnrollmentApplicationStudentForm } from "../EnrollmentApplicationStudent";
import { BaseFieldProps } from "../shared";
import styles from "./field.module.css";

/**
 * Generic props for fields of the EnrollmentApplication model.
 */
type FieldProps<K extends keyof EnrollmentApplicationFormValues.Base = keyof EnrollmentApplicationFormValues.Base> =
	BaseFieldProps<Pick<EnrollmentApplicationFormValues.Base, K>>;

/**
 * Generic props for fields of the EnrollmentApplication model that only appear in the create form.
 */
type CreateFieldProps<
	K extends keyof EnrollmentApplicationFormValues.Create = keyof EnrollmentApplicationFormValues.Create,
> = BaseFieldProps<Pick<EnrollmentApplicationFormValues.Create, K>>;

/**
 * Generic props for fields of the EnrollmentApplication model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof EnrollmentApplicationFormValues.Detail = keyof EnrollmentApplicationFormValues.Detail,
> = BaseFieldProps<Pick<EnrollmentApplicationFormValues.Detail, K>>;

/**
 * Field component for the `createdAt` field of the EnrollmentApplication model.
 */
export function CreatedAt({ formState, ...props }: DetailFieldProps<"createdAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="createdAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `wasInquirySubmitted` field of the EnrollmentApplication model.
 */
export function WasInquirySubmitted({ formState, ...props }: DetailFieldProps<"wasInquirySubmitted">) {
	return <BooleanField formState={formState} name="wasInquirySubmitted" {...props} />;
}

/**
 * Field component for the `inquirySubmittedAt` field of the EnrollmentApplication model.
 */
export function InquirySubmittedAt({ formState, ...props }: DetailFieldProps<"inquirySubmittedAt">) {
	return <DateTimeField formState={formState} name="inquirySubmittedAt" optional precision="minute" {...props} />;
}

/**
 * Field component for the `wasApplicationSubmitted` field of the EnrollmentApplication model.
 */
export function WasApplicationSubmitted({ formState, ...props }: DetailFieldProps<"wasApplicationSubmitted">) {
	return <BooleanField formState={formState} name="wasApplicationSubmitted" {...props} />;
}

/**
 * Field component for the `applicationSubmittedAt` field of the EnrollmentApplication model.
 */
export function ApplicationSubmittedAt({ formState, ...props }: DetailFieldProps<"applicationSubmittedAt">) {
	return <DateTimeField formState={formState} name="applicationSubmittedAt" optional precision="minute" {...props} />;
}

/**
 * Field component for the `source` field of the EnrollmentApplication model.
 */
export function Source({ formState, ...props }: FieldProps<"source">) {
	return <TextField formState={formState} name="source" {...props} />;
}

/**
 * Field component for the `sourceDetails` field of the EnrollmentApplication model.
 */
export function SourceDetails({ formState, ...props }: FieldProps<"sourceDetails">) {
	return <TextField formState={formState} name="sourceDetails" optional {...props} />;
}

/**
 * Field component for the `doesConsentToCorrespondence` field of the EnrollmentApplication model.
 */
export function DoesConsentToCorrespondence({ formState, ...props }: FieldProps<"doesConsentToCorrespondence">) {
	return <BooleanField formState={formState} name="doesConsentToCorrespondence" {...props} />;
}

/**
 * Field component for the `doesConsentToDataUse` field of the EnrollmentApplication model.
 */
export function DoesConsentToDataUse({
	formState,
	noHeader = true,
	className,
	style,
	...props
}: FieldProps<"doesConsentToDataUse">) {
	const id = useUUID("doesConsentToDataUse");

	const showValidation = useCheckShowFieldValidation({
		formType: formState.formType,
		submitCount: formState.submitCount,
		wasTouched: formState.formTouched.doesConsentToDataUse,
		wasChanged: formState.formChanged.doesConsentToDataUse,
		validationStatus: formState.formValidationStatuses.doesConsentToDataUse,
		validationDisplayPolicy: formState.validationDisplayPolicy,
		isFocused: false,
	});
	const hasValidationErrors = formState.formValidationErrors.doesConsentToDataUse.length > 0;

	return (
		<Column justify="spaced-start">
			<If condition={showValidation && hasValidationErrors}>
				<ErrorMessage>You must agree to the Data Use Policy in order to submit your application.</ErrorMessage>
			</If>

			<Row justify="spaced-start" align="center" horizontalSpacing="1rem" className={className} style={style}>
				<BooleanField
					formState={formState}
					name="doesConsentToDataUse"
					id={id}
					noHeader={noHeader}
					optional={false}
					style={{ width: "fit-content", "--boolean-field__value---height": "auto" } as React.CSSProperties}
					{...props}
				/>

				<Label htmlFor={id}>
					I consent to provide Verita International School with the personal data of the parent(s) and student(s) for
					the aforementioned purposes.
				</Label>
			</Row>
		</Column>
	);
}

/**
 * Field component for the `doesConsentToDataProtectionPolicy` field of the EnrollmentApplication model.
 */
export function DoesConsentToDataProtectionPolicy({
	formState,
	noHeader = true,
	className,
	style,
	...props
}: FieldProps<"doesConsentToDataProtectionPolicy">) {
	const id = "doesConsentToDataProtectionPolicy";

	const showValidation = useCheckShowFieldValidation({
		formType: formState.formType,
		submitCount: formState.submitCount,
		wasTouched: formState.formTouched.doesConsentToDataProtectionPolicy,
		wasChanged: formState.formChanged.doesConsentToDataProtectionPolicy,
		validationStatus: formState.formValidationStatuses.doesConsentToDataProtectionPolicy,
		validationDisplayPolicy: formState.validationDisplayPolicy,
		isFocused: false,
	});
	const hasValidationErrors = formState.formValidationErrors.doesConsentToDataProtectionPolicy.length > 0;

	return (
		<Column justify="spaced-start">
			<If condition={showValidation && hasValidationErrors}>
				<ErrorMessage>You must agree to the Data Protection Policy in order to submit your application.</ErrorMessage>
			</If>

			<Row justify="spaced-start" align="center" horizontalSpacing="1rem" className={className} style={style}>
				<BooleanField
					formState={formState}
					name="doesConsentToDataProtectionPolicy"
					id={id}
					noHeader={noHeader}
					optional={false}
					style={{ width: "fit-content", "--boolean-field__value---height": "auto" } as React.CSSProperties}
					{...props}
				/>

				<Label htmlFor={id}>
					I confirm that I have read and agreed with the{" "}
					<ExternalLink href="https://www.veritaschool.ro/wp-content/uploads/2020/04/VERITA-4-ALL_GDPR_POLICY_2020.pdf">
						Data Protection Policy
					</ExternalLink>
					.
				</Label>
			</Row>
		</Column>
	);
}

type ErrorMessageProps = Partial<ChildrenProps>;

function ErrorMessage({ children }: ErrorMessageProps) {
	return (
		<Row justify="spaced-start" align="center" className={styles["field-error-message"]}>
			<Icon.XCircle size={16} className={styles["field-error-message__icon"]} /> {children}
		</Row>
	);
}

/**
 * Field component for the `notes` field of the EnrollmentApplication model.
 */
export function Notes({ formState, ...props }: FieldProps<"notes">) {
	return <TextField formState={formState} name="notes" multiLine optional {...props} />;
}

/**
 * Field component for the `signatureName` field of the EnrollmentApplication model.
 */
export function SignatureName({ formState, ...props }: FieldProps<"signatureName">) {
	return <TextField formState={formState} name="signatureName" {...props} />;
}

/**
 * Field component for the `signatureDate` field of the EnrollmentApplication model.
 */
export function SignatureDate({ formState, ...props }: FieldProps<"signatureDate">) {
	return <DateTimeField formState={formState} name="signatureDate" precision="day" {...props} />;
}

export namespace EnrollmentApplicationGuardians {
	/**
	 * SubForm field component for the `enrollmentApplicationGuardians` edge of the EnrollmentApplication model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"enrollmentApplicationGuardians">) {
		return (
			<SubFormField
				formState={formState}
				name="enrollmentApplicationGuardians"
				blankItem={EnrollmentApplicationGuardianFormValues.blankItemInEnrollmentApplication}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<EnrollmentApplicationGuardianFormValues.CreateInEnrollmentApplication>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Enrollment Application Guardian
				</Button>
				<EnrollmentApplicationGuardianForm.CreateFieldsInEnrollmentApplication formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<EnrollmentApplicationFormValues.Create, "enrollmentApplicationGuardians">,
		EnrollmentApplicationGuardianFormValues.CreateInEnrollmentApplication
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Enrollment Application Guardian
			</Button>
		);
	};
}

export namespace EnrollmentApplicationStudents {
	/**
	 * SubForm field component for the `enrollmentApplicationStudents` edge of the EnrollmentApplication model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"enrollmentApplicationStudents">) {
		return (
			<SubFormField
				formState={formState}
				name="enrollmentApplicationStudents"
				blankItem={EnrollmentApplicationStudentFormValues.blankItemInEnrollmentApplication}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<EnrollmentApplicationStudentFormValues.CreateInEnrollmentApplication>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Enrollment Application Student
				</Button>
				<EnrollmentApplicationStudentForm.CreateFieldsInEnrollmentApplication formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<EnrollmentApplicationFormValues.Create, "enrollmentApplicationStudents">,
		EnrollmentApplicationStudentFormValues.CreateInEnrollmentApplication
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Enrollment Application Student
			</Button>
		);
	};
}
